import { MutationTree } from 'vuex';
import { ICollectionState } from './types';
import {
  IPoster,
  IShard,
  IPrize,
  IMysteryBox,
  IScene,
  IPack,
  ICollectible,
} from '~/types/collections';
import _startCase from 'lodash.startcase';
import {
  IFilmCollectionOverview,
  ISceneStats,
  IFilmMomentStatsOverview,
} from '~/types/my-collection';
import { IGyriUserItem } from '~/types/user-items';

export const mutations: MutationTree<ICollectionState> = {
  updateProjectItems(
    collectionState: ICollectionState,
    payload: { items: IGyriUserItem[]; projectSlug: string },
  ) {
    if (payload) {
      updateState(
        collectionState,
        {
          projectSlug: payload.projectSlug,
        },
        () => {
          collectionState.byProject = {
            ...collectionState.byProject,
            [payload.projectSlug]: {
              ...collectionState.byProject[payload.projectSlug],
              items: payload.items ?? [],
            },
          };
        },
      );
    }
  },

  updateMiscItems(
    collectionState: ICollectionState,
    payload: { items: IGyriUserItem[]; projectSlug: string },
  ) {
    if (payload) {
      collectionState.miscItems = payload.items ?? [];
    }
  },

  updateFilmItems(
    collectionState: ICollectionState,
    payload: { items: IGyriUserItem[]; projectSlug: string },
  ) {
    if (payload) {
      collectionState.filmItems = payload.items ?? [];
    }
  },

  updateProjectStats(
    collectionState: ICollectionState,
    payload: {
      projectStats: IFilmCollectionOverview;
      projectSlug: string;
    },
  ) {
    if (payload) {
      updateState(
        collectionState,
        {
          projectSlug: payload.projectSlug,
        },
        () => {
          collectionState.byProject[payload.projectSlug].projectStatsOverview =
            {
              ...collectionState.byProject[payload.projectSlug]
                .projectStatsOverview,
              ...payload.projectStats,
            };
        },
      );
    }
  },

  updatePosters(
    collectionState: ICollectionState,
    payload: { posters: IPoster[]; projectSlug: string },
  ) {
    if (payload) {
      updateState(
        collectionState,
        {
          projectSlug: payload.projectSlug,
        },
        () => {
          collectionState.byProject[payload.projectSlug].posters =
            payload.posters ?? [];
        },
      );
    }
  },

  updateShards(
    collectionState: ICollectionState,
    payload: { shards: IShard[]; projectSlug: string },
  ) {
    if (payload) {
      updateState(
        collectionState,
        {
          projectSlug: payload.projectSlug,
        },
        () => {
          collectionState.byProject[payload.projectSlug].shards =
            payload.shards ?? [];
        },
      );
    }
  },

  updatePrizes(
    collectionState: ICollectionState,
    payload: { prizes: IPrize[]; projectSlug: string },
  ) {
    if (payload) {
      updateState(
        collectionState,
        {
          projectSlug: payload.projectSlug,
        },
        () => {
          collectionState.byProject[payload.projectSlug].prizes =
            payload.prizes ?? [];
        },
      );
    }
  },

  updateMysteryBoxes(
    collectionState: ICollectionState,
    payload: { mysteryBoxes: IMysteryBox[]; projectSlug: string },
  ) {
    if (payload) {
      updateState(
        collectionState,
        {
          projectSlug: payload.projectSlug,
        },
        () => {
          collectionState.byProject[payload.projectSlug].mysteryBoxes =
            payload.mysteryBoxes ?? [];
        },
      );
    }
  },

  updateMomentPacks(
    collectionState: ICollectionState,
    payload: { momentPacks: IPack[]; projectSlug: string },
  ) {
    if (payload) {
      updateState(
        collectionState,
        {
          projectSlug: payload.projectSlug,
        },
        () => {
          collectionState.byProject[payload.projectSlug].momentPacks =
            payload.momentPacks ?? [];
        },
      );
    }
  },

  updateCollectibles(
    collectionState: ICollectionState,
    payload: { collectibles: ICollectible[]; projectSlug: string },
  ) {
    if (payload) {
      updateState(
        collectionState,
        {
          projectSlug: payload.projectSlug,
        },
        () => {
          collectionState.byProject[payload.projectSlug].collectibles =
            payload.collectibles ?? [];
        },
      );
    }
  },

  updateScenes(
    collectionState: ICollectionState,
    payload: { scenes: IScene[]; projectSlug: string },
  ) {
    if (payload) {
      updateState(
        collectionState,
        {
          projectSlug: payload.projectSlug,
        },
        () => {
          collectionState.byProject[payload.projectSlug].scenes =
            payload.scenes ?? [];
        },
      );
    }
  },

  // updates rewards for A scene and ITS moments
  updateSceneStats(
    collectionState: ICollectionState,
    payload: {
      sceneStats: ISceneStats;
      sceneSlug: string;
      projectSlug: string;
    },
  ) {
    if (payload) {
      updateState(
        collectionState,
        {
          projectSlug: payload.projectSlug,
          sceneSlug: payload.sceneSlug,
        },
        () => {
          const byScene =
            collectionState.byProject[payload.projectSlug].byScene[
              payload.sceneSlug
            ];
          collectionState.byProject[payload.projectSlug].byScene[
            payload.sceneSlug
          ] = { ...byScene, ...(payload.sceneStats ?? {}) };
        },
      );
    }
  },

  updateMomentStats(
    collectionState: ICollectionState,
    payload: {
      momentStats: IFilmMomentStatsOverview;
      momentId: string;
      projectSlug: string;
    },
  ) {
    if (payload) {
      updateState(
        collectionState,
        {
          projectSlug: payload.projectSlug,
          momentId: payload.momentId,
        },
        () => {
          const byMoment =
            collectionState.byProject[payload.projectSlug].byMoment[
              payload.momentId
            ];
          collectionState.byProject[payload.projectSlug].byMoment[
            payload.momentId
          ] = { ...byMoment, ...(payload.momentStats ?? {}) };
        },
      );
    }
  },
};

function updateState(
  collectionState: ICollectionState,
  params: {
    projectSlug: string;
    sceneSlug?: string;
    momentId?: string;
  },
  update: () => void,
): void {
  if (params.projectSlug && !collectionState.byProject[params.projectSlug]) {
    const initProject = {
      projectStatsOverview: {
        lastDayRewards: 0,
        totalRewards: 0,
      },
      byScene: {},
      byMoment: {},
      scenes: [],
      posters: [],

      items: [],
      prizes: [],
      mysteryBoxes: [],
      momentPacks: [],
      collectibles: [],

      moments: [], // todo: not being used remove
      shards: [], // todo: not being used remove
    };
    collectionState.byProject[params.projectSlug] = initProject;
  }

  if (
    params.projectSlug &&
    params.sceneSlug &&
    !collectionState?.byProject?.[params.projectSlug]?.byScene?.[
      params.sceneSlug
    ]
  ) {
    const initScene = {
      distroBoost: 0,
      lastDayRewards: 0,
      totalRewards: 0,
    };
    collectionState.byProject[params.projectSlug].byScene[params.sceneSlug] =
      initScene;
  }

  if (
    params.projectSlug &&
    params.momentId &&
    !collectionState?.byProject?.[params.projectSlug]?.byMoment?.[
      params.momentId
    ]
  ) {
    const initMoment = {
      lastDayRewards: 0,
      totalRewards: 0,
    };
    collectionState.byProject[params.projectSlug].byMoment[params.momentId] =
      initMoment;
  }

  update();
  // collectionState.byProject = { ...collectionState.byProject }
  // collectionState.byProject = JSON.parse(JSON.stringify(collectionState.byProject));
}
